import { useEffect, useState } from 'react';
import { post } from '../../../../api/client';
import { usePermission } from '../../../../hooks/usePermission';
import { PlanGrid } from './PlanGrid';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { PaymentStep } from './PaymentStep';
import { usePaymentStatusPoller } from './usePaymentStatusPoller';
import { useDiscountEstimate } from './useDiscountEstimate';
import type { ChannelItem, CreatePayResult, PlanItem, UpgradeOrder } from './types';
import { fmtDate, fmtPrice } from './utils';

interface Props {
  open: boolean;
  onClose: () => void;
  plans: PlanItem[];
  /** 套餐请求失败（与「后台未配置在售套餐」区分提示） */
  plansFailed?: boolean;
  channels: ChannelItem[];
  /** 支付成功后的回调（刷新会员状态与订单，并关闭弹窗） */
  onPaid: () => Promise<void> | void;
  /** deep-link：预选套餐（按 planCode 匹配） */
  initialPlanCode?: string;
  /** deep-link：预填邀请码 */
  initialReferralCode?: string;
}

/**
 * 会员结算弹窗：选择套餐/支付方式/优惠码 → 创建升级订单 → 发起支付。
 * 支付模式（跳转/扫码/转账/模拟）由后端 create-pay 返回，交给 PaymentStep 渲染。
 * 下单请求体为 camelCase，由后端 RequestInterceptor 转 snake_case 绑定。
 * 套餐一律来自 GET /payment/plans，前端不再内置任何兜底价格——
 * 没有在售套餐时如实提示，而不是拿一个数据库里不存在的价格让用户下单。
 */
export function CheckoutDialog({ open, onClose, plans, plansFailed, channels, onPaid, initialPlanCode, initialReferralCode }: Props) {
  const { isPremium, expireAt } = usePermission();

  const [selectedPlan, setSelectedPlan] = useState(0);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [promotionCode, setPromotionCode] = useState('');
  const [referralCode, setReferralCode] = useState('');

  const [createdOrder, setCreatedOrder] = useState<UpgradeOrder | null>(null);
  const [payResult, setPayResult] = useState<CreatePayResult | null>(null);
  const [upgrading, setUpgrading] = useState(false);
  const [paying, setPaying] = useState(false);
  // 银行转账：用户点「我已完成转账」后才进入「等待管理员核对」态并开始静默轮询
  const [transferSubmitted, setTransferSubmitted] = useState(false);
  const [doneMsg, setDoneMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // 每次打开重置为初始选择状态（deep-link 可预选套餐 / 预填邀请码）
  useEffect(() => {
    if (open) {
      const idx = initialPlanCode ? plans.findIndex((p) => p.planCode === initialPlanCode) : -1;
      setSelectedPlan(idx >= 0 ? idx : 0);
      setSelectedChannel('');
      setCouponCode('');
      setPromotionCode('');
      setReferralCode(initialReferralCode ?? '');
      setCreatedOrder(null);
      setPayResult(null);
      setTransferSubmitted(false);
      setDoneMsg('');
      setErrorMsg('');
      setUpgrading(false);
      setPaying(false);
    }
    // 仅在打开时重置一次；initialPlanCode/initialReferralCode/plans 为 deep-link 稳定入参
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 只有一个可用支付方式时自动选中：渠道是异步加载的，故用独立 effect 跟随 channels，
  // 而不是在打开时的一次性重置里处理。消除「明明只有一个选项却还要求先选」这一多余中间态。
  useEffect(() => {
    if (open && channels.length === 1) {
      const only = channels[0].id;
      setSelectedChannel((cur) => (cur === only ? cur : only));
    }
  }, [open, channels]);

  const plan = plans[selectedPlan] ?? plans[0];
  const planMonths = plan?.durationMonths ?? 1;

  // 下单前优惠试算：校验优惠码有效性并实时展示减免/最终价（无任一优惠码则展示原价）
  const estimate = useDiscountEstimate({
    enabled: open && !createdOrder && plans.length > 0,
    planCode: plan?.planCode,
    planMonths,
    isRenewal: isPremium,
    couponCode,
    promotionCode,
    referralCode,
  });

  // 支付状态轮询：扫码/跳转等实时到账模式主动确认；模拟支付走按钮直连，不轮询。
  // 银行转账是线下非实时到账、只有管理员人工核对才置 paid，故仅在用户点「我已完成转账」后才开始轮询，
  // 避免一进来就显示「正在确认支付结果」误导成实时到账。
  const pollEnabled =
    Boolean(createdOrder) &&
    payResult != null &&
    payResult.mode !== 'mock' &&
    (payResult.mode !== 'bank_transfer' || transferSubmitted);
  const { checking, expired, closed } = usePaymentStatusPoller({
    orderId: createdOrder?.id ?? null,
    enabled: pollEnabled,
    onPaid,
  });

  if (!open) return null;

  /** 从支付步骤退回选择步骤。已创建的订单保持 pending（30 分钟内有效），
   *  重新提交会生成新订单，旧单由定时任务自动关闭。 */
  const handleBackToEdit = () => {
    setCreatedOrder(null);
    setPayResult(null);
    setTransferSubmitted(false);
    setErrorMsg('');
    setDoneMsg('');
  };

  const handleCreateOrder = async () => {
    if (!plan) return;
    // 前置校验：未选定支付方式时直接拦下。必须在「下单」之前——
    // 这里是提交订单，等到 create-pay 失败才提示的话，弹窗已切到支付步骤，
    // 用户只能关闭重来，等于白创建了一笔订单。
    if (!selectedChannel) {
      setErrorMsg('请先选择支付方式');
      return;
    }
    setUpgrading(true);
    setErrorMsg('');
    setDoneMsg('');
    setCreatedOrder(null);
    setPayResult(null);
    try {
      const body: Record<string, unknown> = { planMonths, isRenewal: isPremium };
      if (plan.planCode) body.planCode = plan.planCode;
      if (selectedChannel) body.channelId = selectedChannel;
      if (couponCode.trim()) body.couponCode = couponCode.trim();
      if (promotionCode.trim()) body.promotionCode = promotionCode.trim();
      if (referralCode.trim()) body.referralCode = referralCode.trim();

      const order = await post<UpgradeOrder>('/payment/upgrade', body);
      setCreatedOrder(order);

      try {
        const pay = await post<CreatePayResult>('/payment/create-pay', {
          orderId: order.id,
          channelId: selectedChannel || undefined,
        });
        setPayResult(pay);
        if (pay.mode === 'redirect' && pay.payUrl) {
          setDoneMsg('订单创建成功，正在跳转支付页面…');
          setTimeout(() => {
            window.location.href = pay.payUrl;
          }, 800);
        } else if (pay.mode === 'bank_transfer') {
          setDoneMsg('订单创建成功，请按账户信息完成转账');
        } else if (pay.mode === 'qrcode') {
          setDoneMsg('订单创建成功，请使用微信扫码支付');
        } else {
          setDoneMsg('订单创建成功！请完成支付');
        }
      } catch (e) {
        // create-pay 失败必须透出真实原因（典型是「请选择支付渠道」）。
        // 这里曾把失败静默改写成 mode:'mock'，界面据此显示「当前为模拟支付模式」，
        // 真实模式下反而把失败原因藏了起来，用户会误以为是系统配置问题。
        setErrorMsg('发起支付失败：' + (e instanceof Error ? e.message : '请稍后重试'));
      }
    } catch (e) {
      setErrorMsg('创建订单失败：' + (e instanceof Error ? e.message : '请稍后重试'));
    } finally {
      setUpgrading(false);
    }
  };

  const handleMockPay = async () => {
    if (!createdOrder) return;
    setPaying(true);
    setErrorMsg('');
    try {
      await post('/payment/mock-pay', { orderId: createdOrder.id });
      setDoneMsg('支付成功！会员已激活');
      await onPaid();
    } catch (e) {
      setErrorMsg('支付失败：' + (e instanceof Error ? e.message : '请稍后重试'));
    } finally {
      setPaying(false);
    }
  };

  // 银行转账：用户确认「已完成线下转账」→ 进入等待管理员核对态并开始静默轮询。
  // 无后端动作：待核对提醒已在 create-pay 时发出，此按钮仅承载前端 UX 分期。
  const handleTransferSubmitted = () => setTransferSubmitted(true);

  // 续费后的预计到期日
  const projectedExpire = (() => {
    if (!isPremium || !expireAt || !plan) return null;
    const base = new Date(expireAt);
    base.setMonth(base.getMonth() + planMonths);
    return base;
  })();

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* 头部 */}
        <div className="bg-gradient-to-r from-amber-400 to-yellow-400 px-6 py-5 relative">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
              <i className="ri-vip-crown-2-fill text-white text-xl" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{isPremium ? '续费会员' : '升级会员'}</h2>
              <p className="text-amber-100 text-xs mt-0.5">解锁全部高级功能，提升工作效率</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-lg cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {createdOrder ? (
            <PaymentStep
              order={createdOrder}
              plan={plan}
              payResult={payResult}
              paying={paying}
              onMockPay={handleMockPay}
              transferSubmitted={transferSubmitted}
              onSubmitTransfer={handleTransferSubmitted}
              checking={checking}
              expired={expired}
              closed={closed}
              onBack={handleBackToEdit}
              onClose={onClose}
            />
          ) : plans.length === 0 ? (
            <div className="py-10 text-center">
              <i className="ri-vip-crown-line text-4xl text-gray-200" />
              <p className="mt-4 text-sm text-gray-600">
                {plansFailed
                  ? '套餐加载失败，请关闭后重新打开重试'
                  : '暂无可购买套餐，请联系管理员在后台配置会员套餐'}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 px-5 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
              >
                关闭
              </button>
            </div>
          ) : (
            <>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">选择套餐</p>
                <PlanGrid plans={plans} selectedIndex={selectedPlan} onSelect={setSelectedPlan} />
              </div>

              <PaymentMethodSelector channels={channels} selectedId={selectedChannel} onSelect={setSelectedChannel} />

              {/* 优惠码输入 */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">优惠券码（可选）</label>
                  <input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="输入优惠券码"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-300 text-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">推广码（可选）</label>
                  <input
                    value={promotionCode}
                    onChange={(e) => setPromotionCode(e.target.value)}
                    placeholder="输入推广码"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-300 text-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">邀请码（可选）</label>
                  <input
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    placeholder="输入好友邀请码"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-300 text-gray-700"
                  />
                </div>
              </div>

              {/* 优惠试算反馈：预校验结果 / 减免明细 / 校验中 */}
              {estimate.error && (
                <div className="text-xs px-3 py-2 rounded-lg bg-rose-50 text-rose-600">
                  <i className="ri-error-warning-line mr-1" />
                  {estimate.error}
                </div>
              )}
              {!estimate.error && estimate.data && estimate.data.discountAmount > 0 && (
                <div className="text-xs px-3 py-2 rounded-lg bg-green-50 text-green-700">
                  <i className="ri-coupon-3-line mr-1" />
                  优惠已生效，立减 ¥{fmtPrice(estimate.data.discountAmount)}
                  {estimate.data.appliedType === 'referral' && '（邀请减免）'}
                </div>
              )}
              {estimate.loading && !estimate.error && (
                <div className="text-xs px-3 py-2 text-gray-400">
                  <i className="ri-loader-4-line animate-spin mr-1" />
                  正在校验优惠码…
                </div>
              )}

              {projectedExpire && (
                <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 text-xs text-amber-700">
                  <i className="ri-information-line mr-1" />
                  续费后有效期将延长至：<span className="font-semibold ml-1">{fmtDate(projectedExpire)}</span>
                </div>
              )}

              <div className="flex items-center justify-between gap-4 pt-1">
                <div>
                  <p className="text-xs text-gray-400">应付金额</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-2xl font-bold text-amber-600">
                      ¥{fmtPrice(estimate.data?.finalAmount ?? plan?.price ?? 0)}
                    </p>
                    {estimate.data && estimate.data.discountAmount > 0 && (
                      <span className="text-xs text-gray-400 line-through">
                        ¥{fmtPrice(estimate.data.originalAmount)}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCreateOrder}
                  disabled={upgrading || !selectedChannel}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-amber-500 text-white text-sm font-semibold rounded-xl transition-colors enabled:hover:bg-amber-600 enabled:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {upgrading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      创建订单中…
                    </>
                  ) : (
                    <>
                      <i className="ri-vip-crown-line text-sm" />
                      提交订单 ¥{fmtPrice(estimate.data?.finalAmount ?? plan?.price ?? 0)}
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {errorMsg && (
            <div className="text-xs px-3 py-2 rounded-lg bg-rose-50 text-rose-600">
              <i className="ri-error-warning-line mr-1" />
              {errorMsg}
            </div>
          )}
          {doneMsg && !createdOrder && (
            <div className="text-xs px-3 py-2 rounded-lg bg-teal-50 text-teal-600">
              <i className="ri-checkbox-circle-line mr-1" />
              {doneMsg}
            </div>
          )}
          {!createdOrder && (
            <p className="text-xs text-center text-gray-400">付款即视为同意服务协议 · 到期后自动恢复基础权限</p>
          )}
        </div>
      </div>
    </div>
  );
}
